/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Box, Card, Center, Group, Loader, Paper, SimpleGrid, Stack, Table, Text, Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconCash, IconGift, IconReceipt, IconTicket } from '@tabler/icons-react';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { aggregateGiftcardSales, getSantiagoRange } from '../utils/giftcardSalesReport';

const dateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const currentMonthRange = () => {
  const now = new Date();
  return [new Date(now.getFullYear(), now.getMonth(), 1), now];
};

const money = (value) => `$ ${Number(value || 0).toLocaleString('es-CL')}`;

const MetricCard = ({ icon: Icon, label, value, note, color = 'indigo' }) => (
  <Card withBorder p="md" radius="lg">
    <Group align="flex-start" wrap="nowrap">
      <Box p={8} bg={`${color}.0`} c={`${color}.7`} style={{ borderRadius: 10, lineHeight: 0 }}>
        <Icon size={19} />
      </Box>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{label}</Text>
        <Text size="xl" fw={800}>{value}</Text>
        {note ? <Text size="xs" c="dimmed">{note}</Text> : null}
      </Stack>
    </Group>
  </Card>
);

const CampaignCard = ({ item }) => (
  <Card withBorder p="md">
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={3} style={{ minWidth: 0 }}>
          <Text fw={800}>{item.campaign}</Text>
          <Text size="xs" c="dimmed">{item.type} · {item.origin}</Text>
        </Stack>
        <Badge variant="light" color={item.coupon === 'Sin cupón' ? 'gray' : 'indigo'}>{item.coupon}</Badge>
      </Group>
      <SimpleGrid cols={2} spacing="xs">
        <Box><Text size="xs" c="dimmed">Vendidas</Text><Text fw={700}>{item.paidUnits}</Text></Box>
        <Box><Text size="xs" c="dimmed">Promocionales</Text><Text fw={700}>{item.promotionalUnits}</Text></Box>
        <Box><Text size="xs" c="dimmed">Cobrado</Text><Text fw={700}>{money(item.collectedAmount)}</Text></Box>
        <Box><Text size="xs" c="dimmed">Valor entregado</Text><Text fw={700}>{money(item.deliveredValue)}</Text></Box>
      </SimpleGrid>
      {item.incompleteRecords ? <Text size="xs" c="orange.8">{item.incompleteRecords} registro(s) sin monto disponible</Text> : null}
    </Stack>
  </Card>
);

export default function GiftcardSalesReport() {
  const [[startDate, endDate], setDates] = useState(currentMonthRange);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const mobile = useMediaQuery('(max-width: 48em)');
  const startKey = dateKey(startDate);
  const endKey = dateKey(endDate);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!startKey || !endKey || startKey > endKey) {
        setError('La fecha desde debe ser anterior o igual a la fecha hasta.');
        setActivities([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const { start, endExclusive } = getSantiagoRange(startKey, endKey);
        const snapshot = await getDocs(query(
          collection(db, 'giftcardActivity'),
          where('occurredAt', '>=', start),
          where('occurredAt', '<', endExclusive),
          orderBy('occurredAt', 'desc')
        ));
        if (active) setActivities(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      } catch (loadError) {
        console.error('Error al cargar reporte de giftcards:', loadError);
        if (active) setError('No se pudo cargar el reporte para este periodo.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [startKey, endKey]);

  const report = useMemo(() => aggregateGiftcardSales(activities), [activities]);

  return (
    <Stack gap="lg">
      <Paper withBorder p={{ base: 'md', md: 'lg' }} radius="lg">
        <Group justify="space-between" align="flex-end" gap="md">
          <Stack gap={3}>
            <Title order={3}>Ventas por periodo</Title>
            <Text size="sm" c="dimmed">Resultados agrupados por campaña, cupón y origen.</Text>
          </Stack>
          <Group grow={mobile} w={mobile ? '100%' : 'auto'}>
            <DateInput label="Desde" value={startDate} onChange={(value) => setDates([value, endDate])} valueFormat="DD/MM/YYYY" clearable={false} maxDate={endDate || undefined} />
            <DateInput label="Hasta" value={endDate} onChange={(value) => setDates([startDate, value])} valueFormat="DD/MM/YYYY" clearable={false} minDate={startDate || undefined} />
          </Group>
        </Group>
      </Paper>

      {error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}

      {loading ? (
        <Card withBorder><Center py="xl"><Group><Loader size="sm" /><Text fw={600}>Calculando ventas...</Text></Group></Center></Card>
      ) : (
        <>
          <SimpleGrid cols={{ base: 1, xs: 2, lg: 5 }} spacing="sm">
            <MetricCard icon={IconReceipt} label="Unidades vendidas" value={report.paidUnits} note={`${report.giftcardUnits} giftcards · ${report.prepaidUnits} prepagados`} />
            <MetricCard icon={IconGift} label="Promocionales" value={report.promotionalUnits} note="Separadas de ventas" color="violet" />
            <MetricCard icon={IconTicket} label="Valor entregado" value={money(report.deliveredValue)} color="blue" />
            <MetricCard icon={IconCash} label="Monto cobrado" value={money(report.collectedAmount)} color="teal" />
            <MetricCard icon={IconTicket} label="Descuentos" value={money(report.discountAmount)} color="orange" />
          </SimpleGrid>

          {report.incompleteRecords ? (
            <Alert color="orange" icon={<IconAlertCircle size={18} />}>
              {report.incompleteRecords} registro(s) históricos no tienen montos suficientes. Se contabilizan como unidades, sin inventar valores.
            </Alert>
          ) : null}

          <Card withBorder p={{ base: 'md', md: 'lg' }}>
            <Group justify="space-between" mb="md">
              <Text fw={800}>Detalle por campaña</Text>
              <Badge variant="light" color="gray">{report.groups.length} grupos</Badge>
            </Group>
            {report.groups.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">No hay giftcards emitidas en este periodo.</Text>
            ) : mobile ? (
              <Stack gap="sm">{report.groups.map((item) => <CampaignCard key={item.key} item={item} />)}</Stack>
            ) : (
              <Table.ScrollContainer minWidth={940}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead><Table.Tr>
                    <Table.Th>Campaña</Table.Th><Table.Th>Tipo</Table.Th><Table.Th>Cupón</Table.Th><Table.Th>Origen</Table.Th>
                    <Table.Th ta="right">Vendidas</Table.Th><Table.Th ta="right">Promocionales</Table.Th>
                    <Table.Th ta="right">Valor entregado</Table.Th><Table.Th ta="right">Cobrado</Table.Th><Table.Th ta="right">Descuento</Table.Th>
                  </Table.Tr></Table.Thead>
                  <Table.Tbody>{report.groups.map((item) => (
                    <Table.Tr key={item.key}>
                      <Table.Td><Text fw={700}>{item.campaign}</Text>{item.incompleteRecords ? <Text size="xs" c="orange.8">{item.incompleteRecords} sin monto</Text> : null}</Table.Td>
                      <Table.Td>{item.type}</Table.Td><Table.Td>{item.coupon}</Table.Td><Table.Td>{item.origin}</Table.Td>
                      <Table.Td ta="right">{item.paidUnits}</Table.Td><Table.Td ta="right">{item.promotionalUnits}</Table.Td>
                      <Table.Td ta="right">{money(item.deliveredValue)}</Table.Td><Table.Td ta="right">{money(item.collectedAmount)}</Table.Td><Table.Td ta="right">{money(item.discountAmount)}</Table.Td>
                    </Table.Tr>
                  ))}</Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}
